
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Search, PlusSquare, Heart, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

const BottomNavigation = () => {
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  
  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Navigation items data with gradients and active states
  const navItems = [
    {
      path: "/",
      label: "Home",
      icon: Home,
      gradientFrom: "rgb(59, 130, 246)",
      gradientTo: "rgb(139, 92, 246)",
      activeTextColor: "text-blue-500",
      requiresAuth: false
    },
    {
      path: "/populist-scan",
      label: "Explore",
      icon: Search,
      gradientFrom: "rgb(245, 158, 11)",
      gradientTo: "rgb(239, 68, 68)",
      activeTextColor: "text-amber-500",
      requiresAuth: false
    },
    {
      path: "/analyze",
      label: "Create",
      icon: PlusSquare,
      gradientFrom: "rgb(16, 185, 129)",
      gradientTo: "rgb(20, 184, 166)",
      activeTextColor: "text-emerald-500",
      requiresAuth: true,
      fallbackPath: "/auth"
    },
    {
      path: "/pif-insight/topics",
      label: "Insight",
      icon: Heart,
      gradientFrom: "rgb(139, 92, 246)",
      gradientTo: "rgb(217, 70, 239)",
      activeTextColor: "text-purple-500",
      requiresAuth: false
    },
    {
      path: isAdmin ? "/admin" : "/profile",
      label: isAdmin ? "Admin" : "Profile",
      icon: User,
      gradientFrom: "rgb(37, 99, 235)",
      gradientTo: "rgb(59, 130, 246)",
      activeTextColor: "text-blue-500",
      requiresAuth: true,
      fallbackPath: "/auth"
    }
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t shadow-sm h-14 px-2">
      <div className="flex justify-around items-center h-full">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const path = item.requiresAuth && !user ? (item.fallbackPath || "/auth") : item.path;
          
          return (
            <Link 
              key={item.label} 
              to={path} 
              className="flex flex-col items-center justify-center"
            >
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200",
                active 
                  ? `bg-gradient-to-br from-${item.gradientFrom} to-${item.gradientTo}` 
                  : "bg-transparent"
              )}>
                <Icon 
                  className={cn(
                    "h-5 w-5 transition-colors", 
                    active ? "text-white" : item.activeTextColor
                  )}
                />
              </div>
              <span className={cn(
                "text-[10px] mt-0.5 transition-colors",
                active ? "text-primary font-medium" : "text-muted-foreground"
              )}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default BottomNavigation;
